import { removeWmsLayer } from '../../../common/functions/remove_wms_layer.function';
import mapboxgl from 'mapbox-gl';
import { geoServerLinks } from '../../../common/functions/get_geoServer_links.function';

mapboxgl.accessToken = process.env.REACT_APP_PUBLIC_MAP_BOX_GL_ACCESS_TOKEN;

const vendorColorMap = {
  jazz: '#EF4444',
  wateen: '#2563EB',
  mppl: '#8B5CF6',
  ptcl: '#10B981',
  wtl: '#F97316',
  nayatel: '#CA8A04',
};

const getLayerKey = (value) => (value ? String(value).trim().toLowerCase() : '');

export const addWmsLayer = async (
  map,
  currentKmzNetwork,
  currentPopup,
  stepRef,
  showBackdrop,
  hideBackdrop,
  controller,
  inventoryData,
  showColorCoding,
  linksData = null // Accept pre-fetched data
) => {
  try {
    if (map.current.getSource('linksLayers')) {
      removeWmsLayer(map);
    }
    if (!map.current.getSource('linksLayers')) {
      let data = linksData;

      // Only fetch if data not provided
      if (!data) {
        showBackdrop();
        data = await geoServerLinks(currentKmzNetwork, controller.signal);

        if (!data) {
          console.log('request aborted by user or no data returned');
          hideBackdrop();
          return;
        }

        if (!data.features || data.features.length === 0) {
          console.log('No features in geoserver data');
          hideBackdrop();
          return;
        }
        hideBackdrop();
      }

      if (!data.features || data.features.length === 0) {
        console.log('No features in links data');
        return;
      }

      // Create inventory map by layer_name (similar to LeasedFiberPair)
      const inventoryByLayer = new Map();
      (inventoryData || []).forEach((item) => {
        const layerKey = getLayerKey(item.layer_name);
        if (layerKey && !inventoryByLayer.has(layerKey)) {
          inventoryByLayer.set(layerKey, item);
        }
        const trailKey = getLayerKey(item.trail_name);
        if (trailKey && !inventoryByLayer.has(trailKey)) {
          inventoryByLayer.set(trailKey, item);
        }
      });

      // Process features and assign vendor from inventory
      // Always process features even if inventory data is empty
      if (data && data.features) {
        let matchedCount = 0;
        const processedFeatures = data.features.map((feature) => {
          const layerName =
            feature.properties?.layer_name ??
            feature.properties?.layerName ??
            feature.properties?.trail_name ??
            feature.properties?.trailName;
          const layerKey = getLayerKey(layerName);
          const matched = inventoryByLayer.get(layerKey);

          if (matched) {
            matchedCount++;
          }

          const vendor =
            (matched?.vendor ?? feature.properties?.vendor ?? '').toString().trim() || '';
          const vendorKey = vendor.toLowerCase();
          // Default to green if no vendor found
          const color = vendorColorMap[vendorKey] || '#247d42';

          return {
            ...feature,
            properties: {
              ...feature.properties,
              vendor: vendor,
              assignedColor: color,
            },
          };
        });

        data.features = processedFeatures;
      }

      map.current.addSource('linksLayers', {
        type: 'geojson',
        data: data,
      });

      map.current.addLayer({
        id: 'linksLayers',
        type: 'line',
        source: 'linksLayers',
        paint: {
          'line-color': showColorCoding ? ['get', 'assignedColor'] : '#247d42',
          'line-opacity': ['case', ['==', ['downcase', ['get', 'color']], 'green'], 0.4, 1],
          'line-width': 3,
        },
      });

      map.current.addLayer({
        id: 'linksLayers-dashed',
        type: 'line',
        source: 'linksLayers',
        paint: {
          'line-color': showColorCoding ? ['get', 'assignedColor'] : '#247d42',
          'line-width': 3,
          'line-dasharray': [0, 4, 3],
        },
      });

      // Set initial visibility (will be controlled by showFiberCables toggle)
      map.current.setLayoutProperty('linksLayers', 'visibility', 'visible');
      map.current.setLayoutProperty('linksLayers-dashed', 'visibility', 'visible');

      const dashArraySequence = [
        [0, 4, 3],
        [0.5, 4, 2.5],
        [1, 4, 2],
        [1.5, 4, 1.5],
        [2, 4, 1],
        [2.5, 4, 0.5],
        [3, 4, 0],
        [0, 0.5, 3, 3.5],
        [0, 1, 3, 3],
        [0, 1.5, 3, 2.5],
        [0, 2, 3, 2],
        [0, 2.5, 3, 1.5],
        [0, 3, 3, 1],
        [0, 3.5, 3, 0.5],
      ];

      const animateDashArray = (timestamp) => {
        const newStep = parseInt((timestamp / 120) % dashArraySequence.length);

        map.current.setPaintProperty(
          'linksLayers-dashed',
          'line-dasharray',
          dashArraySequence[stepRef.current]
        );
        stepRef.current = newStep;

        requestAnimationFrame(animateDashArray);
      };

      // animateDashArray(0);
      if (map?.current.getLayer('linksLayers-dashed')) {
        animateDashArray(0);
      }

      map.current.on('mouseenter', 'linksLayers', (e) => {
        map.current.getCanvasContainer().style.cursor = 'pointer';

        const feature = map.current.queryRenderedFeatures(e.point, {
          layers: ['linksLayers'],
        })[0];

        if (!feature) {
          return;
        }

        const properties = feature.properties;
        const lng = e.lngLat;

        // Vendor is already stored in properties when features were processed
        // Also try to get from inventory if available
        const layerName =
          properties?.layer_name ??
          properties?.layerName ??
          properties?.trail_name ??
          properties?.trailName;
        const layerKey = getLayerKey(layerName);
        const matched = inventoryByLayer.get(layerKey);

        // Use vendor from properties (already assigned) or from matched inventory item
        const vendor = properties?.vendor || matched?.vendor || '--';

        const tooltipHtml = `
    <div class="mapTooltip-container">
      <div class="title_container_tooltip">
        <div class="title_text_container_tooltip">
          <h3 class="tooltip-heading">${properties?.layer_name || layerName || '--'}</h3>
          <p class="tooltip-item"><strong>Vendor:</strong> ${vendor}</p>
        </div>
      </div>
    </div>
  `;

        if (currentPopup) {
          currentPopup.remove();
        }

        currentPopup = new mapboxgl.Popup({ closeButton: false })
          .setLngLat(lng)
          .setHTML(tooltipHtml)
          .addTo(map.current);
      });

      map.current.on('mouseleave', 'linksLayers', () => {
        if (currentPopup) {
          currentPopup.remove();
          currentPopup = null;
        }
      });

      map.current.on('mouseleave', 'linksLayers', () => {
        map.current.getCanvasContainer().style.cursor = '';
      });

      hideBackdrop();
    }
  } catch (error) {
    console.error('Error adding WFS layer:', error);
    hideBackdrop();
  }
};
